import type { LaunchContext, LaunchChannel, LaunchClientType } from "./LaunchContextTypes";
import { BootDataError } from "../retry/BootDataError";
import { BOOT_REQUEST_TIMEOUT_MS } from "../retry/BootRequestTimeout";
import { BootHttpError } from "../retry/BootHttpError";

type FixtureName =
  | "fixture_desktop"
  | "fixture_mobile"
  | "fixture_unknown"
  | "fixture_tablet";

/**
 * Controls whether the fixture `LaunchContext` carries a fake `operatorId`.
 * Only affects fixture mode — remote-URL launch contexts are untouched.
 */
export enum FixtureOperatorIdMode {
  /** Fixture context has `operatorId: null`. The config override / URL query can win. Default. */
  None = "none",
  /** Fixture context carries `operatorId: "testOperator"`. Useful for exercising the branding fetch path. */
  TestOperator = "test_operator",
}

export interface LaunchContextLoadOptions {
  fixtureOperatorIdMode?: FixtureOperatorIdMode;
}

export class LaunchContextLoader {
  public async load(
    url: string,
    options: LaunchContextLoadOptions = {},
  ): Promise<LaunchContext> {
    const fixture = this.tryResolveFixture(url, options);
    if (fixture) {
      this.validateOrThrow(fixture);
      return fixture;
    }

    const res = await fetch(url, { method: "GET", signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS) });
    if (!res.ok) throw await BootHttpError.of("LaunchContextLoader", res);

    const json = (await res.json()) as unknown;
    if (!json || typeof json !== "object") {
      throw new BootDataError("LaunchContextLoader: invalid json payload (expected object)");
    }

    const obj = json as Partial<LaunchContext>;
    this.validateOrThrow(obj);
    return obj as LaunchContext;
  }

  private tryResolveFixture(
    url: string,
    options: LaunchContextLoadOptions,
  ): LaunchContext | null {
    const fixtureName = url as FixtureName;
    const operatorIdMode =
      options.fixtureOperatorIdMode ?? FixtureOperatorIdMode.None;

    switch (fixtureName) {
      case "fixture_desktop":
        return this.createFixtureContext({
          clientType: "desktop",
          channel: "desktop",
          preferredOrientation: "landscape",
          operatorIdMode,
        });

      case "fixture_mobile":
        return this.createFixtureContext({
          clientType: "mobile",
          channel: "mobile",
          preferredOrientation: "portrait",
          operatorIdMode,
        });

      case "fixture_tablet":
        return this.createFixtureContext({
          clientType: "tablet",
          channel: "mobile",
          preferredOrientation: "portrait",
          operatorIdMode,
        });

      case "fixture_unknown":
        return this.createFixtureContext({
          clientType: "unknown",
          channel: "unknown",
          preferredOrientation: "portrait",
          operatorIdMode,
        });

      default:
        return null;
    }
  }

  private createFixtureContext(input: {
    clientType: LaunchClientType;
    channel: LaunchChannel;
    preferredOrientation: "portrait" | "landscape" | "any";
    operatorIdMode: FixtureOperatorIdMode;
  }): LaunchContext {
    const operatorId =
      input.operatorIdMode === FixtureOperatorIdMode.TestOperator
        ? "testOperator"
        : null;

    return {
      clientType: input.clientType,
      channel: input.channel,
      source: "backend",
      rawClientType: input.clientType,
      rawChannel: input.channel,
      viewportHint: {
        preferredOrientation: input.preferredOrientation,
      },
      operatorId,
      aggregatorId: "fixture-aggregator",
      platformId: "fixture-platform",
      jurisdictionCode: null,
      localeOverride: null,
      capabilities: {
        supportsDesktopChannel: true,
        supportsMobileChannel: true,
        iframeEmbedded: true,
        parentControlsOrientation: false,
      },
    };
  }

  private validateOrThrow(obj: Partial<LaunchContext>): void {
    if (typeof obj.clientType !== "string" || obj.clientType.length === 0) {
      throw new BootDataError("LaunchContextLoader: missing/invalid clientType");
    }

    const validClientTypes = new Set(["mobile", "tablet", "desktop", "unknown"]);
    if (!validClientTypes.has(obj.clientType)) {
      throw new BootDataError("LaunchContextLoader: invalid clientType");
    }

    if (obj.channel !== undefined) {
      const validChannels = new Set(["mobile", "desktop", "unknown"]);
      if (typeof obj.channel !== "string" || !validChannels.has(obj.channel)) {
        throw new BootDataError("LaunchContextLoader: invalid channel");
      }
    }

    if (obj.source !== undefined) {
      const validSources = new Set([
        "operator",
        "aggregator",
        "backend",
        "inferred",
        "unknown",
      ]);

      if (typeof obj.source !== "string" || !validSources.has(obj.source)) {
        throw new BootDataError("LaunchContextLoader: invalid source");
      }
    }
  }
}